import type {
  MaterialNodeSpec,
  Constituent,
  Quantity,
  QuantityType,
  Unit,
  SymbolicQuantity,
  Container,
  Checkpoint,
  TransferRow,
} from '../../graph/domain/types';
import type { RecommendedInstrumentRef } from '../../components/modals/AddStepModal/types';

// ── Form-level types (used by UI + action) ──────────────────────────────────

export interface TransferRowFormState {
  id: string;
  mode: 'numeric' | 'symbolic';
  amount: number | null;
  unit: Unit | null;
  symbolic: SymbolicQuantity | null;
  destinationMaterialId: string | null;
  destinationDraft: NewDestinationDraft | null;
}

export interface NewDestinationDraft {
  name: string;
  container: Container;
}

export interface TransferFormState {
  quantityType: QuantityType;
  transferEntireVolume: boolean;
  sameVolume: boolean;
  rows: TransferRowFormState[];
  notes: string;
  recommendedInstrument: RecommendedInstrumentRef[];
  durationHours: number;
  durationMinutes: number;
  durationSeconds: number;
  linkToPreviousStep: boolean;
  linkedStepId: string | null;
  checkpoints: Checkpoint[];
}

// ── Unit conversion ─────────────────────────────────────────────────────────

const VOLUME_TO_ML: Record<string, number> = {
  nL: 1e-6,
  µL: 1e-3,
  mL: 1,
  L: 1000,
};

const MASS_TO_G: Record<string, number> = {
  µg: 1e-6,
  mg: 1e-3,
  g: 1,
  kg: 1000,
};

function toFiniteNumber(value: unknown, fallback = 0): number {
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function hasPositiveTransferredAmount(
  source: MaterialNodeSpec,
  row: TransferRowFormState,
): boolean {
  if (row.mode !== 'numeric' || row.amount == null || row.unit == null) {
    return false;
  }
  if (!source.quantity) return false;

  const requested = Math.max(0, toFiniteNumber(row.amount));
  if (requested <= 0) return false;

  const sourceAmount = Math.max(0, toFiniteNumber(source.quantity.value.amount));
  if (sourceAmount <= 0) return false;

  const requestedInSourceUnits = convertToUnit(
    requested,
    row.unit,
    source.quantity.value.units,
  );
  const effectiveTransferred = Math.min(sourceAmount, requestedInSourceUnits);
  return effectiveTransferred > 0;
}

export function convertToUnit(
  amount: number,
  fromUnit: Unit,
  toUnit: Unit,
): number {
  if (fromUnit === toUnit) return amount;

  // Volume family
  if (fromUnit in VOLUME_TO_ML && toUnit in VOLUME_TO_ML) {
    return (amount * VOLUME_TO_ML[fromUnit]) / VOLUME_TO_ML[toUnit];
  }

  // Mass family
  if (fromUnit in MASS_TO_G && toUnit in MASS_TO_G) {
    return (amount * MASS_TO_G[fromUnit]) / MASS_TO_G[toUnit];
  }

  // Count is a single unit; cross-family conversion is not supported
  return amount;
}

export function quantityTypeFromUnit(unit: Unit): QuantityType {
  if (unit in VOLUME_TO_ML) return 'Volume';
  if (unit in MASS_TO_G) return 'Weight';
  return 'Count';
}

export function defaultUnitForType(type: QuantityType): Unit {
  switch (type) {
    case 'Volume':
      return 'mL';
    case 'Weight':
      return 'g';
    case 'Count':
      return 'count';
  }
}

// ── Constituent merge ───────────────────────────────────────────────────────

export function mergeConstituents(
  destinationConstituents: Constituent[],
  sourceConstituents: Constituent[],
): Constituent[] {
  const merged = new Map<string, number>();
  for (const c of destinationConstituents) merged.set(c.name, c.weight);
  for (const c of sourceConstituents) {
    merged.set(c.name, (merged.get(c.name) ?? 0) + c.weight);
  }
  const total =
    Array.from(merged.values()).reduce((s, w) => s + w, 0) || 1;
  return Array.from(merged.entries()).map(([name, w]) => ({
    name,
    weight: w / total,
  }));
}

// ── Quantity computation ────────────────────────────────────────────────────

export function computeSourcePostQuantity(
  source: MaterialNodeSpec,
  row: TransferRowFormState,
): Quantity | null {
  if (!source.quantity) return null;
  if (row.mode !== 'numeric') return null;

  const sourceUnit = source.quantity.value.units;
  const requestedAmount = toFiniteNumber(row.amount, 0);
  const requestedUnit = row.unit ?? sourceUnit;

  const transferred = convertToUnit(
    requestedAmount,
    requestedUnit,
    sourceUnit,
  );
  const sourceAmount = toFiniteNumber(source.quantity.value.amount);
  const remaining = Math.max(0, sourceAmount - transferred);
  return {
    value: { amount: remaining, units: sourceUnit },
    type: source.quantity.type,
  };
}

export function computeMergedQuantity(
  destQuantity: Quantity | null,
  row: TransferRowFormState,
): Quantity | null {
  if (row.mode !== 'numeric') return null;

  const requestedAmount = toFiniteNumber(row.amount, 0);

  if (!destQuantity) {
    const unit = row.unit ?? 'mL';
    return {
      value: { amount: requestedAmount, units: unit },
      type: quantityTypeFromUnit(unit),
    };
  }

  const requestedUnit = row.unit ?? destQuantity.value.units;

  const transferred = convertToUnit(
    requestedAmount,
    requestedUnit,
    destQuantity.value.units,
  );
  const destAmount = toFiniteNumber(destQuantity.value.amount);
  return {
    value: {
      amount: destAmount + transferred,
      units: destQuantity.value.units,
    },
    type: destQuantity.type,
  };
}

export function transferRowToFormState(
  row: TransferRow,
  options?: { transferEntireVolume?: boolean },
): TransferRowFormState {
  const transferEntireVolume = Boolean(options?.transferEntireVolume);
  return {
    id: row.id,
    mode: 'numeric',
    amount: transferEntireVolume
      ? Number.POSITIVE_INFINITY
      : (row.quantity?.value?.amount ?? null),
    unit: row.quantity?.value?.units ?? null,
    symbolic: row.symbolic ?? null,
    destinationMaterialId: row.destinationMaterialId ?? null,
    destinationDraft: null,
  };
}

// ── Post-state builders ─────────────────────────────────────────────────────

export function buildSourcePostState(
  source: MaterialNodeSpec,
  row: TransferRowFormState,
): MaterialNodeSpec {
  return {
    name: source.name,
    container: { ...source.container, id: '' },
    constituents: [...source.constituents],
    sourceMaterials: [
      {
        sourceVertexId: source.id!,
        name: source.name,
        container: source.container,
        notes: null,
      },
    ],
    quantity:
      row.mode === 'numeric' ? computeSourcePostQuantity(source, row) : null,
    symbolicQuantity: row.mode === 'symbolic' ? 'remainder' : undefined,
    isEmpty: false,
    isReminder: true, // Mark as reminder (remainder of source after transfer)
    stateFlags: source.stateFlags,
  };
}

export function buildDestinationPostState(
  source: MaterialNodeSpec,
  destination: MaterialNodeSpec | null,
  row: TransferRowFormState,
  destinationId: string,
): MaterialNodeSpec {
  const shouldIncludeSourceConstituents =
    row.mode === 'symbolic' || hasPositiveTransferredAmount(source, row);

  // New destination (no pre-state)
  if (!destination) {
    return {
      name: row.destinationDraft?.name ?? 'New Destination',
      container: row.destinationDraft?.container ?? source.container,
      constituents: shouldIncludeSourceConstituents
        ? [...source.constituents]
        : [],
      sourceMaterials: [
        {
          sourceVertexId: source.id!,
          name: source.name,
          container: source.container,
          notes: null,
        },
      ],
      quantity:
        row.mode === 'numeric' ? computeMergedQuantity(null, row) : null,
      symbolicQuantity:
        row.mode === 'symbolic' ? (row.symbolic ?? undefined) : undefined,
      isEmpty: false,
    };
  }

  // Existing destination
  return {
    name: destination.name,
    container: { ...destination.container, id: '' },
    constituents: mergeConstituents(
      destination.constituents,
      shouldIncludeSourceConstituents ? source.constituents : [],
    ),
    sourceMaterials: [
      {
        sourceVertexId: destinationId,
        name: destination.name,
        container: destination.container,
        notes: null,
      },
      {
        sourceVertexId: source.id!,
        name: source.name,
        container: source.container,
        notes: null,
      },
    ],
    quantity:
      row.mode === 'numeric'
        ? computeMergedQuantity(destination.quantity, row)
        : null,
    symbolicQuantity:
      row.mode === 'symbolic' ? (row.symbolic ?? undefined) : undefined,
    isEmpty: false,
    stateFlags: destination.stateFlags,
  };
}

// ── Latest-material check ───────────────────────────────────────────────────

export function isLatestMaterial(
  materialId: string,
  flowEdges: Record<string, { sourceId: string; targetId: string }>,
): boolean {
  return !Object.values(flowEdges).some(
    (edge) => edge.sourceId === materialId,
  );
}

// ── Overflow calculation ────────────────────────────────────────────────────

export interface OverflowResult {
  hasOverflow: boolean;
  totalTransferred: number;
  available: number;
  hasSymbolicRows: boolean;
}

export function computeOverflow(
  source: MaterialNodeSpec,
  rows: TransferRowFormState[],
): OverflowResult | null {
  if (!source.quantity) return null;

  const sourceAmount = toFiniteNumber(source.quantity.value.amount);
  const sourceUnit = source.quantity.value.units;
  let total = 0;
  let hasSymbolicRows = false;

  for (const row of rows) {
    if (row.mode === 'symbolic') {
      hasSymbolicRows = true;
      continue;
    }
    if (row.amount != null && row.unit != null) {
      total += convertToUnit(toFiniteNumber(row.amount), row.unit, sourceUnit);
    }
  }

  return {
    hasOverflow: total > sourceAmount,
    totalTransferred: total,
    available: sourceAmount,
    hasSymbolicRows,
  };
}
